import {
  canonicalizeFactoryMatchOptionTitle,
  normalizeFactoryMatchTitleKey,
  sortFactoryMatchOptionsByDisplayOrder,
} from "../UpholsteryV2/helpers/factory/factory-match-option-order"
import { FactoryMatchOption } from "../Upholstery/types"

export type StockFilter = "in-stock" | "special-order"

export interface DropdownOption {
  label: string
  value: string
  image?: string
  sku?: string
  slug?: string
  compatibility?: Array<{ key: string; values: string }> | null
}

export interface InventoryResultOption {
  id: string
  title: string
  value: string
  sku?: string
  imageSrc?: string
}

export interface InventoryResult {
  id: string
  sku: string
  status: "IN STOCK" | "SPECIAL ORDER"
  isInStock: boolean
  /** Raw stocking value from PDM/Dato, sent to Zoho as LEADCF156. */
  rawStockingStatus: string
  make: string
  model: string
  year: string
  vehicleLabel: string
  trim: string
  bodyType: string
  driverSeatType: string
  passengerSeatType: string
  frontRowLabel: string
  frontRowImage: string
  middleRowLabel?: string
  middleRowImage?: string
  backRowLabel: string
  backRowImage: string
  options: InventoryResultOption[]
}

export function isInStockStatus(status: string | undefined | null): boolean {
  if (!status) return false
  const normalized = status.toLowerCase().replace(/[_-]/g, " ").trim()
  if (normalized === "yes") return true
  if (
    normalized === "no" ||
    normalized === "out of stock" ||
    normalized === "outofstock" ||
    normalized === "special order" ||
    normalized.includes("out of stock")
  ) {
    return false
  }
  return normalized === "in stock" || normalized === "instock"
}

export function lookupStockingStatus(
  stockingStatus: Record<string, string> | undefined,
  sku: string
): string | undefined {
  if (!stockingStatus || !sku) return undefined
  if (sku in stockingStatus) return stockingStatus[sku]
  const lower = sku.toLowerCase()
  const match = Object.entries(stockingStatus).find(
    ([key]) => key.toLowerCase() === lower
  )
  return match?.[1]
}

export function getStockingStatusLabel(
  status: string | undefined | null
): "IN STOCK" | "SPECIAL ORDER" {
  return isInStockStatus(status) ? "IN STOCK" : "SPECIAL ORDER"
}

/** PDM V2 stores a URL string; older Dato rows used nested responsiveImage. */
export function resolveInventoryOptionImageSrc(
  image:
    | FactoryMatchOption["image"]
    | { responsiveImage?: { src?: string } }
    | null
    | undefined
): string | undefined {
  if (!image) return undefined
  if (typeof image === "string") {
    const src = image.trim()
    return src || undefined
  }
  const src = image.responsiveImage?.src?.trim()
  return src || undefined
}

export function mapFactoryMatchOptions(
  options: FactoryMatchOption[] | undefined
): InventoryResultOption[] {
  if (!options?.length) return []

  // Keep first option per title (factory match cards group by title)
  const byTitle = new Map<string, InventoryResultOption>()
  options.forEach((option) => {
    const title = canonicalizeFactoryMatchOptionTitle(option.title || "Option")
    if (byTitle.has(title)) return
    byTitle.set(title, {
      id: option.id,
      title,
      value: option.value || "",
      sku: option.sku,
      imageSrc: resolveInventoryOptionImageSrc(option.image),
    })
  })

  return sortFactoryMatchOptionsByDisplayOrder(Array.from(byTitle.values()))
}

const INVENTORY_LEADING_TILE_TITLES = [
  "perforation",
  "stitch",
  "primary color",
  "insert color",
] as const

function inventoryLeadingTileRank(title: string): number {
  const key = normalizeFactoryMatchTitleKey(title)
  return INVENTORY_LEADING_TILE_TITLES.indexOf(
    key as (typeof INVENTORY_LEADING_TILE_TITLES)[number]
  )
}

/**
 * Inventory result cards only.
 * Perforation, then Stitch, then Primary color, then Insert color, then front, middle, and back row
 * (only when filled), then the remaining factory match tiles in their existing
 * order, with Seat diagram schematic last.
 */
export function buildInventoryResultOptionTiles(result: InventoryResult) {
  const rowTiles: Array<{
    id: string
    title: string
    value: string
    image: string | null
    sku?: string
  }> = []

  if (result.frontRowLabel && result.frontRowLabel !== "—") {
    rowTiles.push({
      id: `${result.id}-front-row`,
      title: "Front Row",
      value: result.frontRowLabel,
      image: result.frontRowImage || null,
    })
  }

  if (result.middleRowLabel && result.middleRowLabel !== "—") {
    rowTiles.push({
      id: `${result.id}-middle-row`,
      title: "Middle Row",
      value: result.middleRowLabel,
      image: result.middleRowImage || null,
    })
  }

  if (result.backRowLabel && result.backRowLabel !== "—") {
    rowTiles.push({
      id: `${result.id}-back-row`,
      title: "Back Row",
      value: result.backRowLabel,
      image: result.backRowImage || null,
    })
  }

  const factoryTiles = result.options.map((option) => ({
    id: option.id,
    title: option.title,
    value: option.value,
    sku: option.sku,
    image: option.imageSrc || null,
  }))

  const leadingTiles = factoryTiles
    .filter((tile) => inventoryLeadingTileRank(tile.title) !== -1)
    .sort(
      (a, b) =>
        inventoryLeadingTileRank(a.title) - inventoryLeadingTileRank(b.title)
    )
  const remainingTiles = factoryTiles.filter(
    (tile) => inventoryLeadingTileRank(tile.title) === -1
  )
  const isSeatDiagram = (title: string) =>
    normalizeFactoryMatchTitleKey(title) === "seat diagram schematic"
  const seatDiagramTiles = remainingTiles.filter((tile) =>
    isSeatDiagram(tile.title)
  )
  const otherTiles = remainingTiles.filter((tile) => !isSeatDiagram(tile.title))

  return [...leadingTiles, ...rowTiles, ...otherTiles, ...seatDiagramTiles]
}
